"use client";

// NettiMarkdown — how netti's words are drawn. netti writes markdown (steps as a
// numbered list, options as a short list, code in a fenced block, a table for a
// real comparison) and this turns it into the chat's own quiet type.
//
// It never touches innerHTML. markdown-it parses (html off, so a tag in the
// text is just text), and its token stream is built straight into React
// elements with keys by position. Two things follow from that:
//   · a word that has already been revealed keeps its element as the answer
//     grows beat by beat, so it never re-animates (the reveal stays a reveal);
//   · nothing the model writes can become markup the page runs.
//
// Links: an internal /path carries you there live (the shell's netti-nav
// listener does the moving, as before); a web address opens in a new tab;
// anything else stays words. Images are never loaded from a model's url; they
// read as a link to where the picture lives.

//
// law_snappy: the parser and the highlighter are their own chunks (~95 KB
// gzipped between them, most of it lowlight's grammars), so the dashboard
// never downloads them just to stand ready. The parser is warmed when the
// rail rises (warmNettiMarkdown, from NettiDock) and the highlighter when a
// code block first appears; until either lands, the words are drawn as plain
// paragraphs and the code as plain ink, in the same type.

import * as React from "react";
import type MarkdownIt from "markdown-it";
import type { NettiLowlight } from "./nettiHighlight";
import "./netti-md.css";

type Tok = ReturnType<MarkdownIt["parse"]>[number];
type Hast = { type: string; value?: string; properties?: { className?: string[] }; children?: Hast[] };

// each engine is loaded once per tab and kept at module level, so every later
// answer is drawn straight into it
let md: MarkdownIt | null = null;
let mdLoading: Promise<MarkdownIt> | null = null;
function loadMd(): Promise<MarkdownIt> {
  mdLoading ??= import("./nettiParser").then((m) => (md = m.parser));
  mdLoading.catch(() => { mdLoading = null; });
  return mdLoading;
}
/** fetch the parser ahead of the first answer (the rail is rising). */
export function warmNettiMarkdown(): void {
  void loadMd().catch(() => { /* plain paragraphs stand in */ });
}
let lowlight: NettiLowlight | null = null;
let hlLoading: Promise<NettiLowlight> | null = null;
function loadLowlight(): Promise<NettiLowlight> {
  hlLoading ??= import("./nettiHighlight").then((m) => (lowlight = m.lowlight));
  hlLoading.catch(() => { hlLoading = null; });
  return hlLoading;
}

const nav = (path: string) => window.dispatchEvent(new CustomEvent("innernet:netti-nav", { detail: { path } }));

/** lowlight's tree → React, every run of text one span carrying the classes above it. */
function hastToReact(n: Hast, classes: string[] = [], key = "0"): React.ReactNode {
  if (n.type === "text") {
    const v = n.value ?? "";
    if (!v) return null;
    return classes.length ? <span key={key} className={classes.join(" ")}>{v}</span> : <React.Fragment key={key}>{v}</React.Fragment>;
  }
  const own = n.type === "element" ? n.properties?.className ?? [] : [];
  return (n.children ?? []).map((c, i) => hastToReact(c, [...classes, ...own], `${key}.${i}`));
}

function highlighted(code: string, lang: string): React.ReactNode {
  if (!lowlight) return code;
  try {
    // auto-detect only when the block is long enough to have a shape; a short
    // unlabelled snippet guessed wrong reads worse than plain ink.
    if (lang && lowlight.registered(lang)) return hastToReact(lowlight.highlight(lang, code) as unknown as Hast);
    if (!lang && code.length > 80) return hastToReact(lowlight.highlightAuto(code) as unknown as Hast);
  } catch {
    /* plain below */
  }
  return code;
}

function CodeBlock({ code, lang }: { code: string; lang: string }): React.JSX.Element {
  const [copied, setCopied] = React.useState(false);
  const [hl, setHl] = React.useState(lowlight);
  React.useEffect(() => {
    if (!hl) void loadLowlight().then(setHl, () => { /* plain ink stays */ });
  }, [hl]);
  const body = React.useMemo(() => (hl ? highlighted(code, lang) : code), [code, lang, hl]);
  const copy = () => {
    void navigator.clipboard?.writeText(code).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    });
  };
  return (
    <div className="nchat-pre">
      {lang && <span className="nchat-pre__lang">{lang}</span>}
      <button type="button" className="nchat-copy" onClick={copy} aria-label={copied ? "copied" : "copy this code"} data-on={copied || undefined}>
        {copied ? (
          <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true"><path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
        ) : (
          <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true"><rect x="5.25" y="5.25" width="8" height="8.5" rx="2" fill="none" stroke="currentColor" strokeWidth="1.3" /><path d="M10.75 3.6V3.5a1.5 1.5 0 0 0-1.5-1.5h-5A1.75 1.75 0 0 0 2.5 3.75v5.5a1.5 1.5 0 0 0 1.5 1.5h.1" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>
        )}
      </button>
      <pre><code className={lang ? `language-${lang}` : undefined}>{body}</code></pre>
    </div>
  );
}

/** words as they land — the same blur-and-rise as before, keyed by position. */
function words(text: string, reveal: boolean, key: string): React.ReactNode {
  if (!reveal) return <React.Fragment key={key}>{text}</React.Fragment>;
  return text.split(/(\s+)/).map((t, i) =>
    t === "" || /^\s+$/.test(t) ? <React.Fragment key={`${key}.${i}`}>{t}</React.Fragment> : <span key={`${key}.${i}`} className="nchat-word">{t}</span>,
  );
}

function safeHref(href: string | null): { kind: "internal" | "external"; href: string } | null {
  if (!href) return null
  if (/^\/(?!\/)/.test(href)) return { kind: "internal", href: href.slice(0, 300) }
  if (/^(https?:\/\/|mailto:)/i.test(href)) return { kind: "external", href }
  return null
}

function build(tokens: Tok[], reveal: boolean, prefix: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let i = 0;
  let n = 0;
  while (i < tokens.length) {
    const t = tokens[i];
    const key = `${prefix}.${n++}`;
    if (t.nesting === 1) {
      let depth = 1;
      let j = i + 1;
      while (j < tokens.length && depth > 0) {
        depth += tokens[j].nesting;
        j++;
      }
      const inner = build(tokens.slice(i + 1, depth === 0 ? j - 1 : j), reveal, key);
      out.push(element(t, inner, key));
      i = j;
      continue;
    }
    if (t.nesting === 0) out.push(leaf(t, reveal, key));
    i++;
  }
  return out;
}

function element(t: Tok, children: React.ReactNode[], key: string): React.ReactNode {
  switch (t.tag) {
    case "p":
      // markdown-it hides the paragraph inside a tight list item; honour it
      return t.hidden ? <React.Fragment key={key}>{children}</React.Fragment> : <p key={key}>{children}</p>;
    case "h1":
    case "h2":
      return <h3 key={key} className="nchat-h">{children}</h3>;
    case "h3":
    case "h4":
    case "h5":
    case "h6":
      return <h4 key={key} className="nchat-h nchat-h--small">{children}</h4>;
    case "ul":
      return <ul key={key}>{children}</ul>;
    case "ol": {
      const start = Number(t.attrGet("start") ?? 1);
      return <ol key={key} start={Number.isFinite(start) ? start : 1}>{children}</ol>;
    }
    case "li":
      return <li key={key}>{children}</li>;
    case "blockquote":
      return <blockquote key={key}>{children}</blockquote>;
    case "table":
      // a wide table scrolls sideways inside its own band rather than pushing the rail
      return <div key={key} className="nchat-table"><table>{children}</table></div>;
    case "thead":
      return <thead key={key}>{children}</thead>;
    case "tbody":
      return <tbody key={key}>{children}</tbody>;
    case "tr":
      return <tr key={key}>{children}</tr>;
    case "th":
    case "td": {
      const align = /text-align:(left|right|center)/.exec(t.attrGet("style") ?? "")?.[1] as "left" | "right" | "center" | undefined;
      return t.tag === "th" ? <th key={key} style={align ? { textAlign: align } : undefined}>{children}</th> : <td key={key} style={align ? { textAlign: align } : undefined}>{children}</td>;
    }
    case "strong":
      return <strong key={key} className="nchat-key">{children}</strong>;
    case "em":
      return <em key={key}>{children}</em>;
    case "s":
      return <s key={key}>{children}</s>;
    case "a": {
      const link = safeHref(t.attrGet("href"));
      if (!link) return <React.Fragment key={key}>{children}</React.Fragment>;
      if (link.kind === "internal")
        return (
          <button key={key} type="button" className="nchat-link" onClick={() => nav(link.href)}>
            {children}
          </button>
        );
      return (
        <a key={key} className="nchat-link nchat-link--out" href={link.href} target="_blank" rel="noopener noreferrer nofollow">
          {children}
        </a>
      );
    }
    default:
      return <React.Fragment key={key}>{children}</React.Fragment>;
  }
}

function leaf(t: Tok, reveal: boolean, key: string): React.ReactNode {
  switch (t.type) {
    case "inline":
      return <React.Fragment key={key}>{build(t.children ?? [], reveal, key)}</React.Fragment>;
    case "text":
      return words(t.content, reveal, key);
    case "softbreak":
      return <React.Fragment key={key}>{" "}</React.Fragment>;
    case "hardbreak":
      return <br key={key} />;
    case "code_inline":
      return <code key={key} className="nchat-code">{t.content}</code>;
    case "fence":
    case "code_block":
      return <CodeBlock key={key} code={t.content.replace(/\n$/, "")} lang={(t.info ?? "").trim().split(/\s+/)[0].toLowerCase()} />;
    case "hr":
      return <hr key={key} className="nchat-hr" />;
    case "image": {
      const src = safeHref(t.attrGet("src"));
      const alt = t.content || "a picture";
      return src?.kind === "external" ? (
        <a key={key} className="nchat-link nchat-link--out" href={src.href} target="_blank" rel="noopener noreferrer nofollow">{alt}</a>
      ) : (
        <React.Fragment key={key}>{alt}</React.Fragment>
      );
    }
    default:
      return t.content ? <React.Fragment key={key}>{t.content}</React.Fragment> : null;
  }
}

/** before the parser lands: paragraphs on a blank line, a break on a newline (as `breaks` draws them). */
function plain(text: string, reveal: boolean): React.ReactNode[] {
  return text.split(/\n{2,}/).map((para, i) => (
    <p key={`p.${i}`}>
      {para.split("\n").map((line, j) => (
        <React.Fragment key={j}>
          {j > 0 && <br />}
          {words(line, reveal, `p.${i}.${j}`)}
        </React.Fragment>
      ))}
    </p>
  ));
}

/** netti's answer, drawn. `reveal` animates words as they land (the live message only). */
export default function NettiMarkdown({ text, reveal = false }: { text: string; reveal?: boolean }): React.JSX.Element {
  const [parser, setParser] = React.useState(md);
  React.useEffect(() => {
    if (!parser) void loadMd().then(setParser, () => { /* plain paragraphs stay */ });
  }, [parser]);
  const nodes = React.useMemo(() => {
    if (!parser) return plain(text, reveal);
    try {
      return build(parser.parse(text, {}), reveal, "m");
    } catch {
      return [text];
    }
  }, [text, reveal, parser]);
  return <div className="nchat-md">{nodes}</div>;
}
